import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { TenantThemeProvider, PoweredByZyntra } from "@/components/TenantThemeProvider";
import { getDefaultBranding, type TenantBranding } from "@/lib/branding.functions";

type ReturnSearch = { session_id?: string };

export const Route = createFileRoute("/checkout/return")({
  component: CheckoutReturnPage,
  validateSearch: (search: Record<string, unknown>): ReturnSearch => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
});

function CheckoutReturnPage() {
  const { session_id } = useSearch({ from: "/checkout/return" });
  const [showSuccess, setShowSuccess] = useState(false);
  const [branding, setBranding] = useState<TenantBranding | null>(null);

  useEffect(() => {
    // No reliable eventId on the return URL — fall back to default ZYNTRA branding.
    // Tickets purchased will reflect each event's tenant in /my-tickets.
    getDefaultBranding().then(setBranding).catch(() => {});
    const t = setTimeout(() => setShowSuccess(true), 1500);
    return () => clearTimeout(t);
  }, []);

  if (!session_id) {
    return (
      <TenantThemeProvider branding={branding}>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-md text-center">
            <h2 className="text-2xl font-bold text-foreground">Sesión no encontrada</h2>
            <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
          </div>
        </div>
      </TenantThemeProvider>
    );
  }

  if (!showSuccess) {
    return (
      <TenantThemeProvider branding={branding}>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Confirmando tu pago...</p>
          </div>
        </div>
      </TenantThemeProvider>
    );
  }

  return (
    <TenantThemeProvider branding={branding}>
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
            <Check className="h-8 w-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">¡Pago confirmado!</h2>
          <p className="mt-2 text-muted-foreground">
            Tus entradas ya están disponibles en "Mis Entradas".
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/my-tickets" className="rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
              Ver mis entradas
            </Link>
            <Link to="/" className="rounded-xl border border-border bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground">
              Explorar más
            </Link>
          </div>
          <PoweredByZyntra className="mt-6" />
        </div>
      </div>
    </TenantThemeProvider>
  );
}
